'use client';

import { Box, Flex, Typography } from '@coolblue-development/freddy';
import { DeliveryCostModal } from '@coolblue-development/next-propositions/client';
import {
  SalesPrice,
  SalesPriceCurrent,
  SalesPriceFormer
} from '@coolblue-development/next-sales-price/client';
import type { SecondChanceProduct } from '@features/add-to-cart-page/typings/Product';
import { EnrichedShoppingCartItem } from '@features/add-to-cart-page/typings/ShoppingCart';
import { useTranslation } from '@i18n/useTranslation';

import { SummaryItemPaymentType } from '../../SummaryStore/SummaryStore.types';
import {
  calculateBusinessPrice,
  calculateFormerPrice,
  calculateFormerPriceLabel,
  calculatePersonalPrice,
  calculateSecondChanceTotalPrice,
  calculateTotalPrice,
  mapPriceDataToProductPriceProps,
  mapQuantity
} from './priceCalculations';
import type { ProductPriceData, ProductPriceProps } from './ProductPrice.types';

export default function ProductPrice(props: ProductPriceProps) {
  const isBusinessCustomer = props.isBusinessCustomer;
  const quantity = mapQuantity(props);
  const configuratorShoppingCartItems = props.configuratorShoppingCartItems ?? [];
  const formerPriceLabel = props.formerPriceLabel;
  const normalizedProduct = mapPriceDataToProductPriceProps(props);
  const isSubscriptionPrice = !!normalizedProduct.subscription;

  if (isSubscriptionPrice) {
    return <SubscriptionProductPrice product={normalizedProduct} />;
  }

  if (normalizedProduct.secondChanceProduct) {
    return (
      <SecondChanceProductPrice
        secondChanceProduct={normalizedProduct.secondChanceProduct}
        isBusinessCustomer={isBusinessCustomer}
        quantity={quantity}
        configuratorShoppingCartItems={configuratorShoppingCartItems}
      />
    );
  }

  return (
    <RegularProductPrice
      product={normalizedProduct}
      isBusinessCustomer={isBusinessCustomer}
      quantity={quantity}
      configuratorShoppingCartItems={configuratorShoppingCartItems}
      formerPriceLabel={formerPriceLabel}
    />
  );
}

function SubscriptionProductPrice({ product }: { product: ProductPriceData }) {
  const { t } = useTranslation('webshop');

  return (
    <Flex direction='column' alignItems='flex-end' flexShrink='0'>
      <Flex>
        <SalesPrice
          size='small'
          currentPrice={
            <SalesPriceCurrent price={product.subscription?.oneTimePaymentAmount ?? 0} />
          }
          additionalLabel={
            <Typography variant='body' component='span' ml={100}>
              {t('addToCart.productSummary.oneTimePayment')}
            </Typography>
          }
        />
      </Flex>

      <Flex>
        <SalesPrice
          size='small'
          currentPrice={
            <SalesPriceCurrent price={product.subscription?.installmentAmount ?? 0} isSubtle />
          }
          additionalLabel={
            <Typography variant='body' component='span' color='subtle' ml={100}>
              {t('addToCart.productSummary.monthlyPayment')}
            </Typography>
          }
        />
      </Flex>

      {product.id !== undefined && <DeliveryCostModal productIds={[product.id]} />}
    </Flex>
  );
}

function SecondChanceProductPrice({
  secondChanceProduct,
  isBusinessCustomer,
  quantity,
  configuratorShoppingCartItems
}: {
  secondChanceProduct: SecondChanceProduct;
  isBusinessCustomer: boolean;
  quantity: number;
  configuratorShoppingCartItems: EnrichedShoppingCartItem[];
}) {
  if (isBusinessCustomer)
    return (
      <Box flexShrink={0}>
        <SalesPrice
          alignment='right'
          size='small'
          variant='business'
          currentPrice={
            <SalesPriceCurrent
              price={calculateBusinessPrice(
                secondChanceProduct.price.includingVat,
                quantity,
                configuratorShoppingCartItems || []
              )}
              priceExcludingVat={calculateBusinessPrice(
                secondChanceProduct.price.excludingVat ?? 0,
                quantity,
                configuratorShoppingCartItems || []
              )}
            />
          }
        />
      </Box>
    );

  return (
    <Box flexShrink={0}>
      <SalesPrice
        size='small'
        currentPrice={
          <SalesPriceCurrent
            price={calculateSecondChanceTotalPrice(
              secondChanceProduct,
              configuratorShoppingCartItems
            )}
          />
        }
      />
    </Box>
  );
}

function RegularProductPrice({
  product,
  isBusinessCustomer,
  quantity,
  configuratorShoppingCartItems,
  formerPriceLabel
}: {
  product: ProductPriceData;
  isBusinessCustomer: boolean;
  quantity: number;
  configuratorShoppingCartItems: EnrichedShoppingCartItem[];
  formerPriceLabel?: string;
}) {
  const { t } = useTranslation('webshop');

  if (isBusinessCustomer)
    return (
      <Box flexShrink={0}>
        <SalesPrice
          alignment='right'
          size='small'
          variant='business'
          currentPrice={
            <SalesPriceCurrent
              price={calculateBusinessPrice(
                product.priceIncludingVat,
                quantity,
                configuratorShoppingCartItems || []
              )}
              priceExcludingVat={calculateBusinessPrice(
                product.priceExcludingVat ?? 0,
                quantity,
                configuratorShoppingCartItems || []
              )}
            />
          }
        />
      </Box>
    );

  const formerPrice = calculateFormerPrice(product, configuratorShoppingCartItems || []);

  return (
    <Box flexShrink={0}>
      <SalesPrice
        size='small'
        variant={
          product.paymentType === SummaryItemPaymentType.Monthly ? 'subscription' : 'regular'
        }
        personalPrice={calculatePersonalPrice(
          product,
          configuratorShoppingCartItems || [],
          quantity
        )}
        currentPrice={
          <SalesPriceCurrent
            price={
              product.priceIncludingVat * quantity +
              (calculateTotalPrice(product.priceIncludingVat, configuratorShoppingCartItems || []) -
                product.priceIncludingVat)
            }
          />
        }
        {...(formerPrice && {
          formerPrice: (
            <SalesPriceFormer
              brandName={product.brand?.name}
              price={formerPrice * quantity}
              prefix={calculateFormerPriceLabel(
                formerPriceLabel,
                product.hasSubProducts,
                configuratorShoppingCartItems.length > 0,
                t('orderBlock.salesPrice.prefix')
              )}
            />
          )
        })}
      />
    </Box>
  );
}
